import { useEffect, useState, useCallback, useRef } from 'react';
import { useConnection, usePushTokenRegistration, useSendbirdChat } from '@sendbird/uikit-react-native';
import { Platform } from 'react-native';
import { useAuth } from 'app/auth/hooks/useAuth';
import { SBTokenService } from 'app/auth/services/SBTokenService';
import { getFCMToken } from 'app/services/NotificationService';
import { IUseSendbirdConnectionReturn } from '../types';
import { Telemetry } from 'app/services/telemetry/Telemetry';

export const useSendbirdConnection = (): IUseSendbirdConnectionReturn => {
    const { connect, disconnect } = useConnection();
    const { registerPushTokenForCurrentUser, unregisterPushTokenForCurrentUser } = usePushTokenRegistration();
    const { sdk } = useSendbirdChat();
    const { user, meData } = useAuth();

    const [token, setToken] = useState<string | null>(null);
    const [isConnecting, setIsConnecting] = useState(false);
    const [isConnected, setIsConnected] = useState(false);
    const [connectionError, setConnectionError] = useState<string | null>(null);
    const [hasInitialized, setHasInitialized] = useState(false);
    const prevUserIdRef = useRef<string | undefined>(undefined);
    const hasCurrentPathway = Boolean(meData?.current_pathway?.id);

    const connectToSendbird = useCallback(async (accessToken: string) => {
        if (!user?.id || isConnecting) {
            Telemetry.log('warning', 'Skipping connection - missing user or already connecting');
            return;
        }
        setIsConnecting(true);
        setConnectionError(null);

        try {
            await connect(user.id, { accessToken });
            const fcmToken = await getFCMToken();
            if (fcmToken) {
                try {
                    await (Platform.OS === 'ios'
                        ? sdk.unregisterAPNSPushTokenAllForCurrentUser()
                        : sdk.unregisterFCMPushTokenAllForCurrentUser());
                } catch (error) {
                    Telemetry.error(error as Error, {
                        event: 'push.cleanup.tokens_failed',
                        domain: 'notifications',
                        context: { source: 'useSendbirdConnection.connectToSendbird' }
                    });
                }
                await registerPushTokenForCurrentUser();
            }
            setIsConnected(true);
        } catch (error) {
            setConnectionError(error instanceof Error ? error.message : 'Connection failed');
            setIsConnected(false);
        } finally {
            setIsConnecting(false);
        }
    }, [connect, registerPushTokenForCurrentUser, user?.id]);

    const refreshToken = useCallback(async () => {
        if (isConnecting || !hasCurrentPathway) {
            return;
        }

        try {
            setConnectionError(null);
            let sbToken = await SBTokenService.getToken();
            if (!sbToken) {
                await SBTokenService.requestToken();
                sbToken = await SBTokenService.getToken();
            }
            if (sbToken) {
                setToken(sbToken);
                await connectToSendbird(sbToken);
            } else {
                throw new Error('Unable to obtain Sendbird token');
            }
        } catch (error) {
            setConnectionError(error instanceof Error ? error.message : 'Token refresh failed');
        }
    }, [connectToSendbird, isConnecting, hasCurrentPathway]);

    const handleLogout = useCallback(() => {
        unregisterPushTokenForCurrentUser()
            .catch(error => Telemetry.error(error as Error, {
                event: 'push.unregister.token_sendbird_failed',
                domain: 'notifications',
                context: { source: 'useSendbirdConnection.logout' }
            }))
            .finally(() => disconnect());
        setToken(null);
        setIsConnected(false);
        setHasInitialized(false);
    }, [unregisterPushTokenForCurrentUser, disconnect]);

    const initializeConnection = useCallback(async () => {
        if (hasInitialized || isConnecting || !hasCurrentPathway) {
            return;
        }

        setHasInitialized(true);

        try {
            const sbToken = await SBTokenService.getToken();

            if (sbToken) {
                setToken(sbToken);
                await connectToSendbird(sbToken);
            } else {
                await refreshToken();
            }
        } catch (error) {
            setConnectionError(error instanceof Error ? error.message : 'Initialization failed');
            setHasInitialized(false);
        }
    }, [hasInitialized, isConnecting, hasCurrentPathway, connectToSendbird, refreshToken]);

    useEffect(() => {
        const prevUserId = prevUserIdRef.current;
        const currentUserId = user?.id;

        if (prevUserId && !currentUserId) {
            handleLogout();
        }

        prevUserIdRef.current = currentUserId;
    }, [user?.id, handleLogout]);

    useEffect(() => {
        if (user?.id && hasCurrentPathway && !hasInitialized && !isConnecting) {
            initializeConnection();
        }
    }, [user?.id, hasCurrentPathway, hasInitialized, isConnecting, initializeConnection]);


    return {
        token,
        isConnecting,
        isConnected,
        connectionError,
        refreshToken,
        initializeConnection,
    };
};
